import React, {useEffect, useState} from 'react';
import axios, {AxiosResponse} from 'axios';
import {Col} from 'react-bootstrap';
import CellLoader from '@/components/loading/CellLoader';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import {Button} from '@/components/ui';
import TableComponent from '@/components/table/Table';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import Icon from 'components/icons/Icon';

interface CeleryQueue {
    name: string;
    messages: number;
    consumers: number;
    state: string;
    worker?: string;
    count?: number;
}

interface CeleryQueueResponse {
    celery_queues: {
        results: CeleryQueue[];
    };
}

interface ApiResponse {
    response: CeleryQueueResponse;
}

const celeryQueueUpdateRequest = (): Promise<AxiosResponse<ApiResponse>> => 
    axios.get<ApiResponse>((window as any).URLS.celeryQueueResource)
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const CeleryQueueView: React.FC = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [celeryQueue, setCeleryQueue] = useState<CeleryQueueResponse | null>(null);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            try {
                const response = await celeryQueueUpdateRequest();
                setCeleryQueue(response.data.response);
            } catch (error) {
                console.error('Failed to fetch celery queue data:', error);
            }
        };
        if (!celeryQueue) {
            fetchData();
        }
    }, [celeryQueue]);

    const queueColumns = [
        {
            id: 'name',
            header: i18n.t('jt_queue_name.text'),
            accessorKey: 'name',
        },
        {
            id: 'messages',
            header: i18n.t('jt_messages.text'),
            accessorKey: 'messages',
        },
        {
            id: 'consumers',
            header: i18n.t('jt_workers.text'),
            accessorKey: 'consumers',
        },
        {
            id: 'state',
            header: i18n.t('jt_state.text'),
            accessorKey: 'state',
        },
    ];

    const workerColumns = [
        {
            id: 'worker',
            header: i18n.t('jt_worker_name.text'),
            accessorKey: 'worker',
        },
        {
            id: 'count',
            header: i18n.t('jt_tasks.text'),
            accessorKey: 'count',
        },
    ];

    const queueData = celeryQueue?.celery_queues?.results || [];
    const workerData = celeryQueue?.celery_queues?.results?.filter(d => d.worker) || [];

    const refreshCeleryQueue = (): void => {
        setCeleryQueue(null);
    };

    if (loadingUserData) {
        return <CellLoader fullHeight={true} />;
    }

    const userCanAccessProcessingQueuesData = hasPermission('fs.background_task.queue.read', userData?.permissions, true);
    if (!userCanAccessProcessingQueuesData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_processing_active.text')}/>
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_processing_active.text')}/>
            {celeryQueue
                ? <div className='d-flex justify-content-end'>
                    <Button
                        variant="success"
                        size="sm"
                        className="mb-2"
                        onClick={() => {
                            refreshCeleryQueue();
                        }}
                    >
                        <Icon name="mdi-refresh" size="sm" className="mr-1" />
                        {i18n.t('jt_refresh.text')}
                    </Button>
                </div>
                : <></>
            }
            {!celeryQueue ? (
                <CellLoader fullHeight={true}/>
            ) : (
                <div className="row">
                    <Col lg={6}>
                        <div className="card card-outline-info">
                            <div className="card-body">
                                <TableComponent data={queueData} columns={queueColumns} customTable={true}/>
                            </div>
                        </div>
                    </Col>
                    <Col lg={6}>
                        <div className="card card-outline-info">
                            <div className="card-body">
                                <TableComponent data={workerData} columns={workerColumns} customTable={true}/>
                            </div>
                        </div>
                    </Col>
                </div>
            )}
        </>
    );
});

export default CeleryQueueView;